import type { ConsoleClientCredits, ConsoleCreditLimit, CreditLimitKind } from '@plato/shared';
import { useState } from 'react';
import { ActionError, ReadOnlyNote, useAction } from '../../components/action.tsx';
import { formatCount, formatDate } from '../../components/format.ts';
import { Loaded } from '../../components/page.tsx';
import { useConsole, useHasRole, useReloadableTenantQuery } from '../../data/console-context.tsx';
import type { ConsoleData, TenantSummary } from '../../data/console-data.ts';
import { creditsToMicros, formatCredits, microsToCredits } from './format.ts';
import { LimitMeter } from './limit-meter.tsx';

const loadLimits = (data: ConsoleData, tenant: TenantSummary) => data.getCreditLimits(tenant.id);

const KINDS: ReadonlyArray<{ value: CreditLimitKind; label: string; subject: string | null }> = [
  { value: 'organization', label: 'One client', subject: 'Organization id' },
  { value: 'plan', label: 'Every client on a plan', subject: 'Plan name' },
  { value: 'default', label: 'Every other client', subject: null },
];

export function ClientLimits() {
  const { query, reload } = useReloadableTenantQuery(loadLimits);
  const canEdit = useHasRole('admin');
  const [draft, setDraft] = useState<LimitDraft | null>(null);
  return (
    <section aria-labelledby="client-limits">
      <h2 id="client-limits">Client limits</h2>
      <p className="hint">
        Monthly credit limits for your clients' chat, reset on the 1st (UTC). Evaluations and the
        try-it chat never count. When a client reaches its limit, its users see the message below.
      </p>
      {!canEdit && <ReadOnlyNote needs="admin" />}
      <Loaded query={query}>
        {(value) => (
          <>
            <ClientTable
              clients={value.clients}
              canEdit={canEdit}
              onSetLimit={(client) =>
                setDraft({ kind: 'organization', subject: client.organization ?? '', credits: '' })
              }
            />
            <LimitList
              limits={value.limits}
              canEdit={canEdit}
              onChanged={reload}
              onEdit={setDraft}
            />
            {canEdit && (
              <LimitForm
                key={JSON.stringify(draft)}
                draft={draft ?? { kind: 'plan', subject: '', credits: '' }}
                onSaved={() => {
                  setDraft(null);
                  reload();
                }}
              />
            )}
          </>
        )}
      </Loaded>
      <LimitMessage />
    </section>
  );
}

function ClientTable(props: {
  clients: readonly ConsoleClientCredits[];
  canEdit: boolean;
  onSetLimit: (client: ConsoleClientCredits) => void;
}) {
  if (props.clients.length === 0) {
    return <p className="muted">No client has used the assistant this month.</p>;
  }
  return (
    <table>
      <caption>This month, most of their limit used first</caption>
      <thead>
        <tr>
          <th scope="col">Client</th>
          <th scope="col">Plan</th>
          <th scope="col" className="numeric">
            Replies
          </th>
          <th scope="col" className="numeric">
            Credits
          </th>
          <th scope="col">Limit</th>
          {props.canEdit && <th scope="col">Actions</th>}
        </tr>
      </thead>
      <tbody>
        {props.clients.map((client) => (
          <tr key={client.organization ?? '(none)'}>
            <td>{client.organization ?? <span className="muted">No organization</span>}</td>
            <td>{client.plan ?? '—'}</td>
            <td className="numeric">{formatCount(client.runs)}</td>
            <td className="numeric">{formatCredits(client.credit_micros)}</td>
            <td>
              <LimitMeter client={client} />
              {client.limit !== null && <span className="hint">{limitSource(client.limit)}</span>}
            </td>
            {props.canEdit && (
              <td>
                {client.organization !== null && (
                  <button type="button" className="quiet" onClick={() => props.onSetLimit(client)}>
                    Set limit
                  </button>
                )}
              </td>
            )}
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function limitSource(limit: ConsoleCreditLimit): string {
  if (limit.kind === 'organization') return 'Its own limit';
  if (limit.kind === 'plan') return `Plan “${limit.subject}”`;
  return 'Default limit';
}

interface LimitDraft {
  readonly kind: CreditLimitKind;
  readonly subject: string;
  readonly credits: string;
}

function LimitList(props: {
  limits: readonly ConsoleCreditLimit[];
  canEdit: boolean;
  onChanged: () => void;
  onEdit: (draft: LimitDraft) => void;
}) {
  const { data, tenant } = useConsole();
  const action = useAction();
  const order: Readonly<Record<CreditLimitKind, number>> = { default: 0, plan: 1, organization: 2 };
  const limits = [...props.limits].sort(
    (a, b) => order[a.kind] - order[b.kind] || a.subject.localeCompare(b.subject),
  );
  if (limits.length === 0) {
    return <p className="muted">No limits set: no client is ever stopped.</p>;
  }
  return (
    <>
      <table>
        <caption>Limits</caption>
        <thead>
          <tr>
            <th scope="col">Applies to</th>
            <th scope="col" className="numeric">
              Credits a month
            </th>
            <th scope="col">Changed</th>
            {props.canEdit && <th scope="col">Actions</th>}
          </tr>
        </thead>
        <tbody>
          {limits.map((limit) => (
            <tr key={`${limit.kind}/${limit.subject}`}>
              <td>{appliesTo(limit)}</td>
              <td className="numeric">{formatCredits(limit.monthly_credit_micros)}</td>
              <td>{formatDate(limit.updated_at)}</td>
              {props.canEdit && (
                <td className="row">
                  <button
                    type="button"
                    className="quiet"
                    onClick={() =>
                      props.onEdit({
                        kind: limit.kind,
                        subject: limit.subject,
                        credits: String(microsToCredits(limit.monthly_credit_micros)),
                      })
                    }
                  >
                    Change
                  </button>
                  <button
                    type="button"
                    className="danger"
                    disabled={action.pending}
                    onClick={() =>
                      void action
                        .run(async () => {
                          await data.removeCreditLimit(tenant.id, limit.kind, limit.subject);
                        })
                        .then((ok) => ok && props.onChanged())
                    }
                  >
                    Remove
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      <ActionError error={action.error} />
    </>
  );
}

function appliesTo(limit: ConsoleCreditLimit): string {
  if (limit.kind === 'organization') return `Client ${limit.subject}`;
  if (limit.kind === 'plan') return `Plan ${limit.subject}`;
  return 'Default: clients with no other limit';
}

function LimitForm(props: { draft: LimitDraft; onSaved: () => void }) {
  const { data, tenant } = useConsole();
  const [draft, setDraft] = useState(props.draft);
  const action = useAction();
  const kind = KINDS.find((option) => option.value === draft.kind) ?? KINDS[0];
  const credits = Number(draft.credits);
  return (
    <form
      className="row wrap inline-form"
      aria-label="Set a limit"
      onSubmit={(event) => {
        event.preventDefault();
        void action
          .run(async () => {
            await data.setCreditLimit(tenant.id, {
              kind: draft.kind,
              subject: draft.kind === 'default' ? '' : draft.subject.trim(),
              monthly_credit_micros: creditsToMicros(credits),
            });
          })
          .then((ok) => ok && props.onSaved());
      }}
    >
      <label>
        Limit for
        <select
          value={draft.kind}
          onChange={(e) => setDraft({ ...draft, kind: e.target.value as CreditLimitKind })}
        >
          {KINDS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </label>
      {kind?.subject != null && (
        <label>
          {kind.subject}
          <input
            required
            maxLength={256}
            value={draft.subject}
            onChange={(e) => setDraft({ ...draft, subject: e.target.value })}
          />
        </label>
      )}
      <label>
        Credits a month
        <input
          type="number"
          required
          min={0}
          step="any"
          inputMode="decimal"
          value={draft.credits}
          onChange={(e) => setDraft({ ...draft, credits: e.target.value })}
        />
      </label>
      <button
        type="submit"
        className="primary"
        disabled={action.pending || !(credits >= 0) || draft.credits === ''}
      >
        Save limit
      </button>
      <ActionError error={action.error} />
    </form>
  );
}

const loadSettings = (data: ConsoleData, tenant: TenantSummary) =>
  data.getAssistantSettings(tenant.id);

function LimitMessage() {
  const { data, tenant } = useConsole();
  const { query, reload } = useReloadableTenantQuery(loadSettings);
  const canEdit = useHasRole('editor');
  const action = useAction();
  return (
    <Loaded query={query}>
      {(settings) => (
        <MessageForm
          key={settings.creditLimitMessage}
          message={settings.creditLimitMessage}
          canEdit={canEdit}
          pending={action.pending}
          error={action.error}
          onSave={(creditLimitMessage) =>
            void action
              .run(async () => {
                await data.updateAssistantSettings(tenant.id, { creditLimitMessage });
              })
              .then((ok) => ok && reload())
          }
        />
      )}
    </Loaded>
  );
}

function MessageForm(props: {
  message: string;
  canEdit: boolean;
  pending: boolean;
  error: string | null;
  onSave: (message: string) => void;
}) {
  const [message, setMessage] = useState(props.message);
  return (
    <form
      className="form"
      onSubmit={(event) => {
        event.preventDefault();
        props.onSave(message.trim());
      }}
    >
      <fieldset disabled={!props.canEdit || props.pending}>
        <label>
          Message at the limit
          <span className="hint">
            Shown in the chat when a client has used its monthly credits.
          </span>
          <textarea
            rows={2}
            maxLength={500}
            required
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </label>
        {props.canEdit && (
          <div className="row">
            <button
              type="submit"
              className="primary"
              disabled={message.trim() === '' || message.trim() === props.message}
            >
              Save message
            </button>
          </div>
        )}
      </fieldset>
      <ActionError error={props.error} />
    </form>
  );
}
